import { createGlobalStyle } from 'styled-components';

const GlobalStyles = createGlobalStyle`
  :root {
    /* Palette de couleurs */
    --primary: #16213e; /* Bleu marine */
    --secondary: #FED136; /* Jaune */
    --accent: #C8102E; /* Rouge */
    
    /* Couleurs complémentaires */
    --success: #28a745;
    --info: #17a2b8;
    --warning: #ffc107;
    --danger: #dc3545;
    --light: #f0f4f8;
    --dark: #0e1420;
    --dark-bg: #0a0a10;
    --white: #ffffff;
    --black: #0b1628;
    --gray-100: #f0f4f8;
    --gray-200: #e1e8f0;
    --gray-300: #cdd6e0;
    --gray-400: #a8b5c5;
    --gray-500: #8494a7;
    --gray-600: #5a6b7f;
    --gray-700: #3d4f63;
    --gray-800: #1e3050;
    --gray-900: #0e1420;
    
    /* Typographie */
    --font-primary: 'Poppins', sans-serif;
    --font-secondary: 'Montserrat', sans-serif;
    
    /* Transitions */
    --transition-fast: 0.3s;
    --transition-medium: 0.5s;
    --transition-slow: 0.8s;
    
    /* Ombres */
    --shadow-sm: 0 2px 4px rgba(0, 0, 0, 0.05);
    --shadow-md: 0 4px 8px rgba(0, 0, 0, 0.08);
    --shadow-lg: 0 10px 20px rgba(0, 0, 0, 0.1);
    
    /* Bordures */
    --border-radius-sm: 4px;
    --border-radius-md: 8px;
    --border-radius-lg: 16px;
    --border-radius-xl: 24px;
    --border-radius-circle: 50%;
    
    /* Espacement */
    --spacing-xs: 0.25rem;
    --spacing-sm: 0.5rem;
    --spacing-md: 1rem;
    --spacing-lg: 1.5rem;
    --spacing-xl: 2rem;
    --spacing-xxl: 3rem;
  }
  
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  html {
    scroll-behavior: smooth;
    font-size: 16px;
    overflow-x: hidden;
    max-width: 100vw;
    background-color: #1a3a5c;
    
    @media (max-width: 768px) {
      font-size: 15px;
    }
    
    @media (max-width: 480px) {
      font-size: 14px;
    }
  }
  
  html.dark-mode {
    background-color: var(--dark);
  }
  
  body {
    font-family: var(--font-primary);
    background-color: #1a3a5c;
    color: var(--white);
    line-height: 1.6;
    overflow-x: hidden;
    max-width: 100vw;
    width: 100%;
    transition: background-color var(--transition-medium), color var(--transition-medium);
  }
  
  body.dark-mode {
    background-color: var(--dark);
    color: var(--light);
  }
  
  a {
    text-decoration: none;
    color: var(--primary);
    transition: color var(--transition-fast), transform var(--transition-fast);
    position: relative;
  }
  
  a:hover {
    color: var(--secondary);
  }
  
  button {
    cursor: pointer;
    font-family: var(--font-primary);
    border: none;
    background: none;
    transition: all var(--transition-fast);
  }
  
  button:focus {
    outline: none;
  }
  
  img {
    max-width: 100%;
    height: auto;
    display: block;
  }
  
  .container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 var(--spacing-lg);
  }
  
  .section {
    padding: 100px 0;
    
    @media (max-width: 768px) {
      padding: 60px 0;
    }
    
    @media (max-width: 480px) {
      padding: 40px 0;
    }
  }
  
  .section-title {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
    text-align: center;
    font-weight: 700;
    color: var(--secondary);
    position: relative;
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.7rem;
    }
  }
  
  .dark-mode .section-title {
    color: var(--secondary);
  }
  
  .section-title::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(to right, var(--black) 0%, var(--black) 33%, var(--secondary) 33%, var(--secondary) 66%, var(--accent) 66%, var(--accent) 100%);
    border-radius: var(--border-radius-sm);
  }
  
  .section-subtitle {
    font-size: 1.2rem;
    margin-bottom: 3rem;
    text-align: center;
    color: var(--gray-600);
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
  }
  
  .dark-mode .section-subtitle {
    color: var(--gray-300);
  }
  
  /* Animations */
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
  
  /* Utilitaires */
  .text-center { text-align: center; }
  .text-left { text-align: left; }
  .text-right { text-align: right; }
  
  .d-flex { display: flex; }
  .flex-column { flex-direction: column; }
  .justify-center { justify-content: center; }
  .align-center { align-items: center; }
  .flex-wrap { flex-wrap: wrap; }
  
  .mt-1 { margin-top: var(--spacing-sm); }
  .mt-2 { margin-top: var(--spacing-md); }
  .mt-3 { margin-top: var(--spacing-lg); }
  .mt-4 { margin-top: var(--spacing-xl); }
  .mt-5 { margin-top: var(--spacing-xxl); }
  
  .mb-1 { margin-bottom: var(--spacing-sm); }
  .mb-2 { margin-bottom: var(--spacing-md); }
  .mb-3 { margin-bottom: var(--spacing-lg); }
  .mb-4 { margin-bottom: var(--spacing-xl); }
  .mb-5 { margin-bottom: var(--spacing-xxl); }
  
  /* Responsive */
  @media (max-width: 1200px) {
    html {
      font-size: 15px;
    }
    
    .container {
      max-width: 960px;
    }
  }
  
  @media (max-width: 992px) {
    html {
      font-size: 14px;
    }
    
    .container {
      max-width: 720px;
    }
    
    .section {
      padding: 80px 0;
    }
    
    .section-title {
      font-size: 2.2rem;
    }
  }
  
  @media (max-width: 768px) {
    html {
      font-size: 14px;
    }
    
    .container {
      max-width: 540px;
      padding: 0 var(--spacing-lg);
    }
    
    .section {
      padding: 60px 0;
    }
    
    .section-title {
      font-size: 2rem;
    }
    
    .section-subtitle {
      font-size: 1.1rem;
    }
    
    /* Amélioration de la navigation sur mobile */
    .d-flex {
      flex-direction: column;
    }
    
    /* Sauf si spécifié autrement */
    .d-flex.flex-row-mobile {
      flex-direction: row;
    }
  }
  
  @media (max-width: 576px) {
    html {
      font-size: 13px;
    }
    
    .section {
      padding: 50px 0;
    }
    
    .section-title {
      font-size: 1.8rem;
    }
    
    .container {
      padding: 0 var(--spacing-md);
    }
    
    /* Ajustements spécifiques pour les très petits écrans */
    .hide-xs {
      display: none;
    }
    
    .text-center-xs {
      text-align: center;
    }
    
    .full-width-xs {
      width: 100%;
    }
  }
  
  /* Correction pour les appareils à haute densité de pixels */
  @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
    body {
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
  }

  /* Couleurs spécifiques pour l'accessibilité */
  body.dark-mode h3 {
    color: var(--secondary);
  }
  body.dark-mode h2.section-title {
    color: var(--secondary);
  }

  @keyframes twinkle {
    0% { opacity: 0.1; transform: scale(0.8); }
    100% { opacity: 0.6; transform: scale(1.2); }
  }
`;

export default GlobalStyles;
